import React from "react";
import { useAppSelector } from "../hooks/hooks";
import { useNavigate } from "react-router-dom";
import { SearchSuggestionsProps } from "./types";

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  isVisible,
  searchQuery,
}) => {
  const { 
    searchSuggestions, 
    isSearching 
  } = useAppSelector((state) => state.appData);
  const navigate = useNavigate();

  if (!isVisible || !searchQuery.trim() || searchQuery.length < 2) {
    return null;
  }

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.type === 'provider') {
      // Navigate to the provider's category
      navigate(`/products/${suggestion.categoryKey}`);
    } else {
      // Navigate to search results for the specific plan
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSeeAllResults = () => {
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  if (isSearching) {
    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm">Searching...</p>
        </div>
      </div>
    );
  }

  if (searchSuggestions.length === 0) {
    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          No products found for "{searchQuery}"
        </div>
      </div>
    );
  }

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
      {searchSuggestions.map((suggestion, index) => (
        <button
          key={suggestion.id}
          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-600 last:border-b-0"
          onClick={() => handleSuggestionClick(suggestion)}
        >
          <div className="flex items-center gap-3">
            {suggestion.image_url && (
              <img 
                src={suggestion.image_url} 
                alt={suggestion.name}
                className="w-8 h-8 rounded-md object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-gray-700 dark:text-gray-300 text-sm font-medium truncate">
                {suggestion.name}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs truncate">
                {suggestion.category}
              </p>
            </div>
          </div>
        </button>
      ))}
      <div className="p-3 border-t border-gray-100 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
        <button
          className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:text-blue-700 dark:hover:text-blue-300"
          onClick={handleSeeAllResults}
        >
          See all results for "{searchQuery}"
        </button>
      </div>
    </div>
  );
};

export default SearchSuggestions;